import type React from 'react';
import { useCallback, useMemo, useState } from 'react';

import ArrowLeft from '@mui/icons-material/ArrowLeft';
import { Button, Grid } from '@mui/material';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import DrawIcon from 'assets/icons/Icon _ Notification visual _ Draw.svg?react';
import StandardButton from 'components/buttons/StandardButton';
import AddPlayerDialog from 'components/dialogs/addPlayerDialog/AddPlayerDialog';
import AddTeamDialog from 'components/dialogs/addTeamDialog/AddTeamDialog';
import DeleteTeamDialog from 'components/dialogs/deleteTeamDialog/DeleteTeamDialog';
import FilteringNoResults from 'components/emptyStates/FilteringNoResults';
import StandardPaper from 'components/papers/standard/StandardPaper';
import EventChip from 'components/tables/common/EventChip';
import EnhancedTable from 'components/tables/enhancedTable/EnhancedTable';
import { actionsHeaderStyles, useStyles as useTableStyles } from 'components/tables/enhancedTable/styles';
import PlayerBulkActions from 'components/tables/players/PlayerBulkActions';
import PlayersFilters from 'components/tables/players/PlayersFilters';
import { playerColumnIds, useTableColumns } from 'components/tables/players/utils';
import { useStyles as useTableCommonStyles } from 'components/tables/styles';
import { BodyLarge, BodyRegularLight, H3, H4 } from 'components/typography/Typography';
import { TEAMS_PART } from 'config/router/constants';
import { getParticipantRoutes } from 'config/router/utils';
import { usePlayersProfileDialog } from 'containers/dialogs/PlayersProfile';
import useMutateTournament from 'hooks/tournament/useMutateTournament';
import { useProgress } from 'hooks/tournament/useSaveTournament';
import useTournamentParticipants from 'hooks/tournament/useTournamentParticipants';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';

import { useStyles } from './styles';

const columnHelper = createColumnHelper<ParticipantInterface>();

const shownColumnIds = ['selection', 'name', 'gender', 'city', 'state', 'actions'];

const SelectedTeamPanel: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { classes } = useStyles();
  const { classes: tableCommonClasses } = useTableCommonStyles();
  const { classes: tableClasses } = useTableStyles();
  const { tournamentId, teamId } = useParams<{ eventId: string; tournamentId: string; teamId: string }>();
  const showProfileDialog = usePlayersProfileDialog();

  const { tournamentParticipants } = useTournamentParticipants();
  const addTeamProgress = useProgress('addTeam');
  const deleteTeamProgress = useProgress('deleteTeam');
  const [removeFromTeam] = useMutateTournament('removePlayerFromTeam');
  const [addTeam] = useMutateTournament('addTeam');
  const [deleteTeam] = useMutateTournament('deleteTeam');

  const [addTeamDialogOpen, setAddTeamDialogOpen] = useState<boolean>(false);
  const [deleteTeamDialogOpen, setDeleteTeamDialogOpen] = useState<boolean>(false);
  const [addPlayersDialogOpen, setAddPlayersDialogOpen] = useState<boolean>(false);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() =>
    playerColumnIds.reduce((o, key) => Object.assign(o, { [`${key}`]: shownColumnIds?.includes(key) }), {}),
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>([]);

  const selectedTeam = useMemo(
    () => tournamentParticipants.find((p) => p.participantId === teamId),
    [teamId, tournamentParticipants],
  );

  const participants = useMemo(
    () =>
      selectedTeam?.individualParticipantIds
        ? tournamentParticipants.filter((p) => selectedTeam.individualParticipantIds.includes(p.participantId))
        : [],
    [selectedTeam, tournamentParticipants],
  );

  const handleGoBack = useCallback(() => {
    navigate(getParticipantRoutes(tournamentId, TEAMS_PART));
  }, [navigate, tournamentId]);

  const handleEditTeamDialogOpen = useCallback(() => {
    setAddTeamDialogOpen(true);
  }, []);

  const handleCloseAddTeamDialog = useCallback(() => {
    setAddTeamDialogOpen(false);
  }, []);

  const isAddTeamDialogOpen = useMemo(
    () => (addTeamProgress ? true : addTeamDialogOpen),
    [addTeamProgress, addTeamDialogOpen],
  );

  const handleDeleteTeamDialogOpen = useCallback(() => {
    setDeleteTeamDialogOpen(true);
  }, []);

  const handleCloseDeleteTeamDialog = useCallback(() => {
    setDeleteTeamDialogOpen(false);
  }, []);

  const isDeleteTeamDialogOpen = useMemo(
    () => (deleteTeamProgress ? true : deleteTeamDialogOpen),
    [deleteTeamProgress, deleteTeamDialogOpen],
  );

  const handleAddPlayersDialogOpen = useCallback(() => {
    setAddPlayersDialogOpen(true);
  }, []);

  const handleAddPlayersDialogClosed = useCallback(() => {
    setAddPlayersDialogOpen(false);
  }, []);

  const handleRemovePlayer = useCallback(
    (rowItem) => {
      removeFromTeam(rowItem?.participantId, rowItem?.participantName, teamId, selectedTeam?.participantName);
    },
    [removeFromTeam, selectedTeam?.participantName, teamId],
  );

  const handleDeleteTeam = useCallback(
    async (teamId: string, eventId: string, teamName: string) => {
      const res = await deleteTeam(teamId, eventId, teamName);
      if (res?.data?.mutateTournament?.success) {
        const nextRoute = getParticipantRoutes(tournamentId, TEAMS_PART);
        navigate(nextRoute);
      }
    },
    [deleteTeam, navigate, tournamentId],
  );

  const defaultColumns = useTableColumns({
    classes: tableCommonClasses,
    openPlayerProfile: showProfileDialog,
    t,
  });

  const columns = useMemo(
    () => [
      ...defaultColumns,
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <StandardButton size="small" onClick={() => handleRemovePlayer(row.original)}>
            {t('remove')}
          </StandardButton>
        ),
        enableGlobalFilter: false,
        meta: {
          headerClasses: [actionsHeaderStyles().className],
          cellClasses: [tableClasses.actionsCell],
        },
      }),
    ],
    [defaultColumns, handleRemovePlayer, t, tableClasses.actionsCell],
  );

  const options: TableOptions<ParticipantInterface> = useMemo(
    () => ({
      columns: columns,
      data: participants,
      enableRowSelection: true,
      enableMultiRowSelection: true,
      enableColumnFilters: true,
      options: {
        autoResetFilters: false,
        autoResetGlobalFilter: false,
      },
      state: {
        columnVisibility,
        rowSelection,
        sorting,
      },
      initialState: {
        pagination: {
          pageIndex: 0,
          pageSize: 25,
        },
      },
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      onColumnVisibilityChange: setColumnVisibility,
      onSortingChange: setSorting,
      onRowSelectionChange: setRowSelection,
    }),
    [columns, columnVisibility, participants, rowSelection, sorting],
  );
  const tableInstance = useReactTable<ParticipantInterface>(options);
  const paginationState = tableInstance.getState().pagination;
  const filters = tableInstance.getState().columnFilters;
  const columnsVisibility = tableInstance.getState().columnVisibility;
  const globalFilter = tableInstance.getState().globalFilter;
  const { setGlobalFilter, setColumnFilters } = tableInstance;
  const selectedFlatRows = tableInstance.getSelectedRowModel().flatRows;
  const { rows } = tableInstance.getRowModel();

  const participantOtherName = selectedTeam?.participantOtherName ? `(${selectedTeam.participantOtherName})` : '';

  return useMemo(() => {
    return (
      <>
        <Grid container direction="row">
          <Grid item>
            <Button
              className={classes.goBackTypography}
              color="primary"
              startIcon={<ArrowLeft className={classes.arrowGoBack} height={14} />}
              variant="text"
              onClick={handleGoBack}
            >
              {t('teams.backToTeams')}
            </Button>
          </Grid>
        </Grid>
        <StandardPaper className={classes.headerPaper}>
          <Grid
            container
            alignItems="flex-start"
            className={classes.teamsPanelInfo}
            direction="row"
            justifyContent="space-between"
          >
            <Grid item className={classes.flexGrow}>
              <H3 className={classes.tablePaperTitle}>
                {selectedTeam?.participantName} {participantOtherName}
              </H3>

              <Grid container className={classes.teamDetail} direction="row" justifyContent="space-between">
                <Grid item>
                  <Grid container direction="column">
                    <Grid item>
                      <BodyRegularLight className={classes.bold}>{t('teams.contactName')}</BodyRegularLight>
                    </Grid>
                    <Grid item>
                      <BodyLarge>{selectedTeam?.contacts?.[0]?.name ?? ''}</BodyLarge>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container direction="column">
                    <Grid item>
                      <BodyRegularLight className={classes.bold}>{t('teams.contactPhone')}</BodyRegularLight>
                    </Grid>
                    <Grid item>
                      <BodyLarge>{selectedTeam?.contacts?.[0]?.telephone ?? ''}</BodyLarge>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container direction="column">
                    <Grid item>
                      <BodyRegularLight className={classes.bold}>{t('teams.contactEmail')}</BodyRegularLight>
                    </Grid>
                    <Grid item>
                      <BodyLarge>{selectedTeam?.contacts?.[0]?.emailAddress ?? ''}</BodyLarge>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container direction="column">
                    <Grid item>
                      <BodyRegularLight className={classes.bold}>{t('evt')}</BodyRegularLight>
                    </Grid>
                    <Grid item>
                      <BodyLarge>
                        {selectedTeam?.events?.map((event) => <EventChip key={event.eventId} event={event} />)}
                      </BodyLarge>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Grid container item className={classes.teamsPanelActions} direction="row" justifyContent="flex-end">
              <Grid item>
                <StandardButton variant="warning" onClick={handleDeleteTeamDialogOpen}>
                  {t('delete')}
                </StandardButton>
              </Grid>
              <Grid item>
                <StandardButton onClick={handleEditTeamDialogOpen}>{t('teams.editDetails')}</StandardButton>
              </Grid>
            </Grid>
          </Grid>
        </StandardPaper>
        <StandardPaper>
          {participants?.length > 0 ? (
            <>
              <Grid container alignItems="flex-start" direction="row" justifyContent="space-between" spacing={1}>
                <Grid item>
                  <H4 className={classes.tablePaperTitle}>{`${t('teams.teamRoster')} (${
                    selectedTeam?.individualParticipantIds?.length
                  })`}</H4>
                  <PlayersFilters
                    filters={filters}
                    globalFilter={globalFilter}
                    setFilters={setColumnFilters}
                    setGlobalFilter={setGlobalFilter}
                  />
                </Grid>
                <Grid item>
                  <StandardButton size="small" variant="primary" onClick={handleAddPlayersDialogOpen}>
                    {t('teams.addPlayers')}
                  </StandardButton>
                </Grid>
              </Grid>
              <EnhancedTable
                columnsVisibility={columnsVisibility}
                id="team-players-table"
                paginationState={paginationState}
                rows={rows}
                selectedActions={<PlayerBulkActions selectedFlatRows={selectedFlatRows} showTeams={false} />}
                tableInstance={tableInstance}
              />
              {!rows.length && <FilteringNoResults />}
            </>
          ) : (
            <FilteringNoResults
              icon={<DrawIcon />}
              noResultsActionText={'teams.addPlayers'}
              noResultsHeader={'teams.noResultsHeader'}
              noResultsText={'teams.noResultsText'}
              onAction={handleAddPlayersDialogOpen}
            />
          )}
        </StandardPaper>
        {isAddTeamDialogOpen && (
          <AddTeamDialog
            editMode={true}
            loading={addTeamProgress}
            open={addTeamProgress ? true : addTeamDialogOpen}
            participant={selectedTeam}
            validEvents={[]}
            onAddTeam={addTeam}
            onClose={handleCloseAddTeamDialog}
          />
        )}
        {isDeleteTeamDialogOpen && (
          <DeleteTeamDialog
            loading={deleteTeamProgress}
            open={isDeleteTeamDialogOpen}
            participant={selectedTeam}
            onClose={handleCloseDeleteTeamDialog}
            onDeleteTeam={handleDeleteTeam}
          />
        )}
        {addPlayersDialogOpen && (
          <AddPlayerDialog
            open={addPlayersDialogOpen}
            team={selectedTeam}
            tournamentParticipants={tournamentParticipants}
            onClose={handleAddPlayersDialogClosed}
          />
        )}
      </>
    );
  }, [
    selectedTeam,
    classes.goBackTypography,
    classes.arrowGoBack,
    classes.headerPaper,
    classes.teamsPanelInfo,
    classes.flexGrow,
    classes.tablePaperTitle,
    classes.teamDetail,
    classes.bold,
    classes.teamsPanelActions,
    handleGoBack,
    t,
    handleDeleteTeamDialogOpen,
    handleEditTeamDialogOpen,
    participants?.length,
    setGlobalFilter,
    filters,
    globalFilter,
    handleAddPlayersDialogOpen,
    rows,
    isAddTeamDialogOpen,
    addTeamProgress,
    addTeamDialogOpen,
    handleCloseAddTeamDialog,
    addTeam,
    tournamentParticipants,
    isDeleteTeamDialogOpen,
    deleteTeamProgress,
    handleCloseDeleteTeamDialog,
    handleDeleteTeam,
    addPlayersDialogOpen,
    handleAddPlayersDialogClosed,
    columnsVisibility,
    paginationState,
    participantOtherName,
    selectedFlatRows,
    setColumnFilters,
    tableInstance,
  ]);
};

export default SelectedTeamPanel;
